import { BudgetLine } from '../../../services/fpa.service';

export interface GridRow { key: string; accountId: string; segmentId: string; departmentId?: string; projectId?: string; amounts: string[] }

/** The (year, month) pairs of a version, `count` months from its start date (12 = one fiscal year). */
export const fiscalMonths = (fiscalYearStart: string, count = 12): { year: number; month: number; label: string }[] => {
    const [y, m] = fiscalYearStart.split('-').map(Number);
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return Array.from({ length: count }, (_, i) => {
        const idx = (m - 1 + i) % 12;
        const year = y + Math.floor((m - 1 + i) / 12);
        return { year, month: idx + 1, label: `${names[idx]} ${String(year).slice(2)}` };
    });
};

/**
 * How many months the grid must show: at least 12, and as many as the stored lines reach (a rolling FORECAST runs past
 * the first fiscal year). Showing every stored month is what keeps a save from dropping the ones a grid never displayed.
 */
export const monthSpan = (fiscalYearStart: string, lines: { year: number; month: number }[]): number => {
    const [y, m] = fiscalYearStart.split('-').map(Number);
    let span = 12;
    for (const l of lines) span = Math.max(span, (l.year - y) * 12 + (l.month - m) + 1);
    return Math.min(span, 36);
};

/** First-of-month ISO date of the first and last displayed month: the window a save is allowed to replace. */
export const windowOf = (months: { year: number; month: number }[]): { from: string; to: string } => {
    const iso = (x: { year: number; month: number }) => `${x.year}-${String(x.month).padStart(2, '0')}-01`;
    return { from: iso(months[0]), to: iso(months[months.length - 1]) };
};

export const rowKey = (accountId: string, segmentId: string, departmentId = '', projectId = '') =>
    (departmentId ? `${accountId}|${segmentId}|${departmentId}` : `${accountId}|${segmentId}`) // B4-H
    + (projectId ? `|p:${projectId}` : ''); // P1-A project

/** Lines -> grid rows (one per account x segment). Order-tagged lines are not editable here and are kept aside. */
export const linesToRows = (lines: BudgetLine[], months: { year: number; month: number }[]): { rows: GridRow[]; tagged: BudgetLine[] } => {
    const map = new Map<string, GridRow>();
    const tagged: BudgetLine[] = [];
    for (const l of lines) {
        if (l.buyerOrderId) { tagged.push(l); continue; }
        const idx = months.findIndex(m => m.year === l.year && m.month === l.month);
        if (idx < 0) continue;
        const key = rowKey(l.accountId, l.segmentId ?? '', l.departmentId ?? '', l.projectId ?? '');
        let row = map.get(key);
        if (!row) { row = { key, accountId: l.accountId, segmentId: l.segmentId ?? '', departmentId: l.departmentId ?? '', projectId: l.projectId ?? '', amounts: Array(months.length).fill('') }; map.set(key, row); }
        row.amounts[idx] = String(l.amount);
    }
    return { rows: Array.from(map.values()), tagged };
};

/** Grid rows -> lines. Blank cells are omitted; non-numeric cells are reported. */
export const rowsToLines = (rows: GridRow[], months: { year: number; month: number }[], tagged: BudgetLine[]): { lines: BudgetLine[]; errors: string[] } => {
    const lines: BudgetLine[] = [...tagged];
    const errors: string[] = [];
    rows.forEach((r, ri) => {
        if (!r.accountId) { if (r.amounts.some(a => a.trim() !== '')) errors.push(`Row ${ri + 1}: choose an account`); return; }
        r.amounts.forEach((a, i) => {
            const t = a.trim().replace(/,/g, '');
            if (t === '') return;
            const n = Number(t);
            if (!Number.isFinite(n)) { errors.push(`Row ${ri + 1}, ${months[i].month}/${months[i].year}: "${a}" is not a number`); return; }
            lines.push({ accountId: r.accountId, segmentId: r.segmentId || null, ...(r.departmentId ? { departmentId: r.departmentId } : {}), ...(r.projectId ? { projectId: r.projectId } : {}), year: months[i].year, month: months[i].month, amount: Math.round(n * 100) / 100 });
        });
    });
    return { lines, errors };
};

/** Splits clipboard text copied from Excel (tabs and newlines) into a 2-D array of cell strings. */
export const parsePaste = (text: string): string[][] =>
    text.replace(/\r/g, '').split('\n').filter((l, i, a) => !(i === a.length - 1 && l === '')).map(l => l.split('\t'));

/** Pastes a block into the grid at (row, col); rows beyond the end are appended as blank account rows. */
export const applyPaste = (rows: GridRow[], row: number, col: number, block: string[][], width = 12): GridRow[] => {
    const next = rows.map(r => ({ ...r, amounts: [...r.amounts] }));
    block.forEach((cells, bi) => {
        const ri = row + bi;
        while (next.length <= ri) next.push({ key: `new-${next.length}-${Date.now()}`, accountId: '', segmentId: '', amounts: Array(width).fill('') });
        cells.forEach((c, bj) => { if (col + bj < width) next[ri].amounts[col + bj] = c.trim(); });
    });
    return next;
};
